import { FC, useEffect, useState } from 'react';

import styles from '../BillingSection.module.scss';
import { ICustomer, ILocationsStatistic, IVoicePlatformAccessStatistic } from '../../../../model';
import { billingCache, getLocationsStatistic, getVoicePlatformAccessStatistic, nexResponseService } from '../../../../services';
import { Spinner } from '../../../common';
import { ApiExceptions } from '../../../../model/api/api-exceptions';
import Asc from "../../../../assets/ArrowAscReg.svg";
import Desc from "../../../../assets/ArrowDescReg.svg";
import {BillingInvoicesSection} from "../Invoices/BillingInvoicesSection";
import {hasNexPark, hasNexResponse} from "../../../../model/api/nexParkInstance/instancesChecker";

interface IBillingSectionProps {
  customer: ICustomer;
  hasBillingAccess: boolean;
}


interface IApiErrorLike {
  response?: { data?: { code?: string } };
}

export const BillingSection: FC<IBillingSectionProps> = ({ customer, hasBillingAccess }) => {
  const customerId = customer.id;
  const [locationsStatistic, setLocationsStatistic] = useState<ILocationsStatistic | undefined>();
  const [nexResponseLocationsStatistic, setNexResponseLocationsStatistic] = useState<ILocationsStatistic | undefined>();
  const [voicePlatformStatistic, setVoicePlatformStatistic] = useState<IVoicePlatformAccessStatistic | undefined>();
  const [loadingState, setLoadingState] = useState<{ loading: boolean; error: string | null }>({ loading: true, error: null });

  const calculatePercentage = (value: number, total: number): number => {
    if (!total) {
      return 0;
    }
    const result = Math.round((value / total) * 100);

    return Number.isNaN(result) ? 0 : result;
  };

  const calculateDifference = (current: number, previous: number): number => {
    const result = current - previous;
    return Number.isNaN(result) ? 0 : result;
  }

  useEffect(() => {
    const controller = new AbortController();

    const fetchData = async () => {
      if (!customerId || (!hasNexPark(customer) && !hasNexResponse(customer))) {
        setLocationsStatistic(undefined);
        setNexResponseLocationsStatistic(undefined);
        setVoicePlatformStatistic(undefined);
        setLoadingState({ loading: false, error: null });
        return;
      }

      if (!hasBillingAccess) {
        setLocationsStatistic(undefined);
        setNexResponseLocationsStatistic(undefined);
        setVoicePlatformStatistic(undefined);
        setLoadingState({ loading: false, error: "Access denied. You don't have the required permissions." });
        return;
      }

      const { nexResponseRegionId } = customer;
      const needsNexPark = hasNexPark(customer);
      const needsNexResponse = hasNexResponse(customer) && !!nexResponseRegionId;

      const nexParkCacheKey = needsNexPark ? billingCache.nexParkKey(customerId) : undefined;
      const nexResponseCacheKey = needsNexResponse ? billingCache.nexResponseKey(customerId, nexResponseRegionId!) : undefined;
      const voicePlatformCacheKey = needsNexPark ? billingCache.voicePlatformKey(customerId) : undefined;

      const cachedNexPark = nexParkCacheKey ? billingCache.read(nexParkCacheKey) : undefined;
      const cachedNexResponse = nexResponseCacheKey ? billingCache.read(nexResponseCacheKey) : undefined;
      const cachedVoicePlatform = voicePlatformCacheKey ? billingCache.readVoicePlatform(voicePlatformCacheKey) : undefined;

      // check full cache
      if ((!needsNexPark || cachedNexPark) && (!needsNexResponse || cachedNexResponse) && (!needsNexPark || cachedVoicePlatform)) {
        setLocationsStatistic(cachedNexPark);
        setNexResponseLocationsStatistic(cachedNexResponse);
        setVoicePlatformStatistic(cachedVoicePlatform);
        setLoadingState({ loading: false, error: null });
        return;
      }

      setLoadingState({ loading: true, error: null });

      // check partial cache
      try {
        let nexParkResult: ILocationsStatistic | undefined = cachedNexPark;
        if (needsNexPark && !nexParkResult) {
          nexParkResult = await getLocationsStatistic(customerId, controller.signal);
          if (nexParkCacheKey && nexParkResult) {
            billingCache.write(nexParkCacheKey, nexParkResult);
          }
        }
        let nexResponseResult: ILocationsStatistic | undefined = cachedNexResponse;
        if (needsNexResponse && !nexResponseResult && nexResponseRegionId) {
          nexResponseResult = await nexResponseService.getLocationsStatistic(customerId, nexResponseRegionId, controller.signal);
          if (nexResponseCacheKey && nexResponseResult) {
            billingCache.write(nexResponseCacheKey, nexResponseResult);
          }
        }
        let voicePlatformResult: IVoicePlatformAccessStatistic | undefined = cachedVoicePlatform;
        if (needsNexPark && !voicePlatformResult) {
          voicePlatformResult = await getVoicePlatformAccessStatistic(customerId, controller.signal);
          if (voicePlatformCacheKey && voicePlatformResult) {
            billingCache.writeVoicePlatform(voicePlatformCacheKey, voicePlatformResult);
          }
        }

        if (controller.signal.aborted) {
          return;
        }

        setLocationsStatistic(nexParkResult);
        setNexResponseLocationsStatistic(nexResponseResult);
        setVoicePlatformStatistic(voicePlatformResult);
      } catch (error) {
        if (controller.signal.aborted) {
          return;
        }

        const apiError = error as IApiErrorLike;
        const errorMessage =
            apiError.response?.data?.code === ApiExceptions.CUSTOMER_HAS_NOT_NEXPARK_URL
                ? 'The Billing is unavailable for this customer. The Billing module does not currently support the connected services.'
                : 'Failed to load data. Please try again later.';

        console.error('Failed to fetch locations statistic:', error);
        setLoadingState({ loading: false, error: errorMessage });
        setLocationsStatistic(undefined);
        setNexResponseLocationsStatistic(undefined);
      } finally {
        if (!controller.signal.aborted) {
          setLoadingState((prevState) => ({ ...prevState, loading: false }));
        }
      }
    };

    fetchData();

    return () => {
      controller.abort();
    };
  }, [customer, customerId, hasBillingAccess]);

  return (
    <div>

      {loadingState.loading && (
        <div style={{ minHeight: 120, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Spinner />
        </div>
      )}
      {loadingState.error && <div className={styles.error}>{loadingState.error}</div>}

      {(locationsStatistic || nexResponseLocationsStatistic) && !loadingState.loading && (
        <>
          <h2 className={styles.summaryTitle}>Metrics</h2>
          <div className={styles.summaryContainer}>
            {/* Locations Card */}
            {locationsStatistic && (
              <div className={styles.card}>
                <div className={styles.cardHeader}>
                  <div className={styles.cardTitle}>NexPark Locations</div>
                </div>
                <div className={styles.statsList}>
                  <div className={styles.statItem}>
                    <span>Total</span>
                    <span className={styles.statValue}>{locationsStatistic.totalLocations}</span>
                  </div>
                  <div className={styles.statItem}>
                    <span>Active</span>
                    <div className={styles.statValueContainer}>
                      <span className={styles.statValue}>{locationsStatistic.activeLocations} ({calculatePercentage(locationsStatistic.activeLocations, locationsStatistic.totalLocations)}%)</span>
                    </div>
                  </div>
                  <div className={styles.statItem}>
                    <span>Billable</span>
                    <div className={styles.statValueContainer}>
                      <span className={styles.statValue}>{locationsStatistic.activeBillableLocations} ({calculatePercentage(locationsStatistic.activeBillableLocations, locationsStatistic.totalLocations)}%)</span>
                    </div>
                  </div>
                  <div className={styles.statItem}>
                    <span>Locations to invoice</span>
                    <span className={styles.statValue}>{locationsStatistic.currentMonthLocationsToInvoice}</span>
                  </div>
                </div>
              </div>
            )}

            {hasNexResponse(customer) && nexResponseLocationsStatistic && (
              <div className={styles.card}>
                <div className={styles.cardHeader}>
                  <div className={styles.cardTitle}>NexResponse Locations</div>
                </div>
                <div className={styles.statsList}>
                  <div className={styles.statItem}>
                    <span>Total</span>
                    <span className={styles.statValue}>{nexResponseLocationsStatistic.totalLocations}</span>
                  </div>
                  <div className={styles.statItem}>
                    <span>Active</span>
                    <div className={styles.statValueContainer}>
                      <span className={styles.statValue}>{nexResponseLocationsStatistic.activeLocations} ({calculatePercentage(nexResponseLocationsStatistic.activeLocations, nexResponseLocationsStatistic.totalLocations)}%)</span>
                    </div>
                  </div>
                  <div className={styles.statItem}>
                    <span>Billable</span>
                    <div className={styles.statValueContainer}>
                      <span className={styles.statValue}>{nexResponseLocationsStatistic.activeBillableLocations} ({calculatePercentage(nexResponseLocationsStatistic.activeBillableLocations, nexResponseLocationsStatistic.totalLocations)}%)</span>
                    </div>
                  </div>
                  <div className={styles.statItem}>
                    <span>Locations to invoice</span>
                    <span className={styles.statValue}>{nexResponseLocationsStatistic.currentMonthLocationsToInvoice}</span>
                  </div>
                </div>
              </div>
            )}

            {/* Lanes Card */}
            {locationsStatistic && (
              <div className={styles.card}>
                <div className={styles.cardHeader}>
                  <div className={styles.cardTitle}>Lanes</div>
                </div>
                <div className={styles.statsList}>
                  <div className={styles.statItem}>
                    <span>Total</span>
                    <span className={styles.statValue}>{locationsStatistic.totalLanes}</span>
                  </div>
                  <div className={styles.statItem}>
                    <span>Enabled</span>
                    <div className={styles.statValueContainer}>
                      <span className={styles.statValue}>{locationsStatistic.enabledLanes} ({calculatePercentage(locationsStatistic.enabledLanes, locationsStatistic.totalLanes)}%)</span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Calls Card - Current Month */}
            {locationsStatistic && (
              <div className={styles.card}>
                <div className={styles.cardHeader}>
                  <div className={styles.cardTitle}>Calls (Current Month)</div>
                </div>
                <div className={styles.statsList}>
                  <div className={styles.statItem}>
                    <span>Total Calls</span>
                    <span className={styles.statValue}>{locationsStatistic.currentMonthTotalCalls}</span>
                  </div>
                  <div className={styles.statItem}>
                    <span>Total Duration</span>
                    <span className={styles.statValue}>{locationsStatistic.currentMonthTotalDuration} sec</span>
                  </div>
                </div>
              </div>
            )}

            {/* Locations Changes Card */}
            {locationsStatistic && (
              <div className={styles.card}>
                <div className={styles.cardHeader}>
                  <div className={styles.cardTitle}>Locations Changes (Current Month)</div>
                </div>
                <div className={styles.statsList}>
                  <div className={styles.statItem}>
                    <span>Added</span>
                    <span className={styles.statValue}>{locationsStatistic.locationsAddedThisMonth}</span>
                  </div>
                  <div className={styles.statItem}>
                    <span>Deactivated</span>
                    <span className={styles.statValue}>{locationsStatistic.locationsDeactivatedThisMonth}</span>
                  </div>
                </div>
              </div>
            )}

            {/* Agents Card - Current and Previous Month */}
            {locationsStatistic && (
              <div className={styles.card}>
                <div className={styles.cardHeader}>
                  <div className={styles.cardTitle}>Agents</div>
                </div>
                <table className={styles.agentMetricTable}>
                  <thead>
                    <tr>
                      <th></th>
                      <th className={styles.tableThTitle}>This Month</th>
                      <th className={styles.tableThTitle}>Last Month</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>Total</td>
                      <td style={{fontWeight: 600}}>{locationsStatistic.currentMonthTotalAgents ?? 0}</td>
                      <td style={{color: '#525150'}}>{locationsStatistic.previousMonthTotalAgents ?? 0}</td>
                      <td>
                        {calculateDifference(locationsStatistic.currentMonthTotalAgents, locationsStatistic.previousMonthTotalAgents) > 0 ? (
                          <div style={{ display: 'flex', alignItems: 'center' }}>
                            <img src={Asc} alt={'Asc stat icon'} />
                            <span className={styles.ascStat}>{calculateDifference(locationsStatistic.currentMonthTotalAgents, locationsStatistic.previousMonthTotalAgents)}</span>
                          </div>
                        ) : (
                          <div style={{ display: 'flex', alignItems: 'center' }}>
                            <img src={Desc} alt={'Desc stat icon'} />
                            <span className={styles.descStat}>{calculateDifference(locationsStatistic.currentMonthTotalAgents, locationsStatistic.previousMonthTotalAgents)}</span>
                          </div>
                        )}
                      </td>
                    </tr>
                    <tr>
                      <td>Active</td>
                      <td style={{fontWeight: 600}}>{locationsStatistic.currentMonthActiveAgents ?? 0}</td>
                      <td style={{color: '#525150'}}>{locationsStatistic.previousMonthActiveAgents ?? 0}</td>
                      <td>
                        {calculateDifference(locationsStatistic.currentMonthActiveAgents, locationsStatistic.previousMonthActiveAgents) > 0 ? (
                            <div style={{ display: 'flex', alignItems: 'center' }}>
                              <img src={Asc} alt={'Asc stat icon'} />
                              <span className={styles.ascStat}>{calculateDifference(locationsStatistic.currentMonthActiveAgents, locationsStatistic.previousMonthActiveAgents)}</span>
                            </div>
                        ) : (
                            <div style={{ display: 'flex', alignItems: 'center' }}>
                              <img src={Desc} alt={'Desc stat icon'} />
                              <span className={styles.descStat}>{calculateDifference(locationsStatistic.currentMonthActiveAgents, locationsStatistic.previousMonthActiveAgents)}</span>
                            </div>
                        )}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            )}

            {/* Voice Platform Access Card */}
            {voicePlatformStatistic && (
              <div className={styles.card}>
                <div className={styles.cardHeader}>
                  <div className={styles.cardTitle}>Voice Platform Access</div>
                </div>
                <table className={styles.agentMetricTable}>
                  <thead>
                    <tr>
                      <th></th>
                      <th className={styles.tableThTitle}>This Month</th>
                      <th className={styles.tableThTitle}>Last Month</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>Mobile access</td>
                      <td style={{fontWeight: 600}}>{voicePlatformStatistic.currentMonthMobileAccess ?? 0}</td>
                      <td style={{color: '#525150'}}>{voicePlatformStatistic.previousMonthMobileAccess ?? 0}</td>
                      <td>
                        {calculateDifference(voicePlatformStatistic.currentMonthMobileAccess, voicePlatformStatistic.previousMonthMobileAccess) > 0 ? (
                          <div style={{ display: 'flex', alignItems: 'center' }}>
                            <img src={Asc} alt={'Asc stat icon'} />
                            <span className={styles.ascStat}>{calculateDifference(voicePlatformStatistic.currentMonthMobileAccess, voicePlatformStatistic.previousMonthMobileAccess)}</span>
                          </div>
                        ) : (
                          <div style={{ display: 'flex', alignItems: 'center' }}>
                            <img src={Desc} alt={'Desc stat icon'} />
                            <span className={styles.descStat}>{calculateDifference(voicePlatformStatistic.currentMonthMobileAccess, voicePlatformStatistic.previousMonthMobileAccess)}</span>
                          </div>
                        )}
                      </td>
                    </tr>
                    <tr>
                      <td>Platform users</td>
                      <td style={{fontWeight: 600}}>{voicePlatformStatistic.currentMonthVoicePlatformUsers ?? 0}</td>
                      <td style={{color: '#525150'}}>{voicePlatformStatistic.previousMonthVoicePlatformUsers ?? 0}</td>
                      <td>
                        {calculateDifference(voicePlatformStatistic.currentMonthVoicePlatformUsers, voicePlatformStatistic.previousMonthVoicePlatformUsers) > 0 ? (
                          <div style={{ display: 'flex', alignItems: 'center' }}>
                            <img src={Asc} alt={'Asc stat icon'} />
                            <span className={styles.ascStat}>{calculateDifference(voicePlatformStatistic.currentMonthVoicePlatformUsers, voicePlatformStatistic.previousMonthVoicePlatformUsers)}</span>
                          </div>
                        ) : (
                          <div style={{ display: 'flex', alignItems: 'center' }}>
                            <img src={Desc} alt={'Desc stat icon'} />
                            <span className={styles.descStat}>{calculateDifference(voicePlatformStatistic.currentMonthVoicePlatformUsers, voicePlatformStatistic.previousMonthVoicePlatformUsers)}</span>
                          </div>
                        )}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
      <BillingInvoicesSection customer={customer} hasBillingAccess={hasBillingAccess}></BillingInvoicesSection>
    </div>
  );
};
